import { Box, FormControl, FormLabel, Text, HStack } from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { Select as ChakraReactSelect, SingleValue } from "chakra-react-select";

// Components
import CustomModal from "../../../../shared/Modals/CustomModal";

// Hooks
import useClaimsToast from "../../../../hooks/useToast";
import { useReservations } from "../../../../hooks/useReservations";

// Utils
import { getClaimReservationFormData } from "../../../../utils/api/reservations";
import {
	GetClaimReservationFormDataResponse,
	BenefitCodeOption,
	PassGroupOption,
	ClaimsStatus
} from "../../../../utils/api/protos/messages/claims.message_pb";
import { State } from "../../../../utils/api/protos/messages/core.message_pb";
import { getClaimStatusFromBffStatus } from "../../../../utils/helpers";

interface CreateReservationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	defaultUserId?: number; // Pre-populate with user from detail page
	userName?: string; // Display name of pre-selected user
}

// Type for select options
interface SelectOption {
	label: string;
	value: string;
}

/**
 * Modal for creating a new claim reservation
 */
export default function CreateReservationModal({
	isOpen,
	onClose,
	onSuccess,
	defaultUserId,
	userName
}: CreateReservationModalProps) {
	const toast = useClaimsToast();
	const { createReservation, isLoading } = useReservations();

	// Form state - initialize with defaultUserId if provided
	const [userId, setUserId] = useState<number>(defaultUserId || 0);
	const [passGroupId, setPassGroupId] = useState<string>("");
	const [benefitCode, setBenefitCode] = useState<string>("");
	const [state, setState] = useState<string>("");
	const [claimStatus, setClaimStatus] = useState<ClaimsStatus | undefined>(undefined);

	// Selected options for type-ahead
	const [selectedPassGroup, setSelectedPassGroup] = useState<SingleValue<SelectOption>>(null);
	const [selectedState, setSelectedState] = useState<SingleValue<SelectOption>>(null);
	const [selectedBenefitCode, setSelectedBenefitCode] = useState<SingleValue<SelectOption>>(null);
	const [selectedClaimStatus, setSelectedClaimStatus] = useState<SingleValue<SelectOption>>(null);

	// Form dropdown options
	const [passGroupOptions, setPassGroupOptions] = useState<SelectOption[]>([]);
	const [stateOptions, setStateOptions] = useState<SelectOption[]>([]);
	const [benefitCodeOptions, setBenefitCodeOptions] = useState<SelectOption[]>([]);
	const [claimStatusOptions, setClaimStatusOptions] = useState<SelectOption[]>([]);
	const [isLoadingFormData, setIsLoadingFormData] = useState<boolean>(false);

	// Update userId when defaultUserId changes
	useEffect(() => {
		if (defaultUserId) {
			setUserId(defaultUserId);
		}
	}, [defaultUserId]);

	// Load form dropdown data on mount
	useEffect(() => {
		const fetchFormData = async () => {
			setIsLoadingFormData(true);
			try {
				const response: GetClaimReservationFormDataResponse = await getClaimReservationFormData();

				// Extract all fields from the response
				const passGroupsList = response.getPassGroupsList();
				const statesList = response.getStatesList();
				const benefitCodesList = response.getBenefitCodesList();
				const claimStatusesList = response.getClaimStatusesList();

				// Transform to select options format
				const passGroupOpts: SelectOption[] = passGroupsList.map((pg: PassGroupOption) => ({
					label: pg.getPassGroupId(),
					value: pg.getPassGroupId()
				}));

				const stateOpts: SelectOption[] = statesList.map((s: State) => ({
					label: `${s.getCode()} - ${s.getName()}`,
					value: s.getCode()
				}));

				const benefitCodeOpts: SelectOption[] = benefitCodesList.map((bc: BenefitCodeOption) => ({
					label: `${bc.getMatterBillingCode()} - ${bc.getMatterTitle()}`,
					value: bc.getMatterBillingCode()
				}));

				const claimStatusOpts: SelectOption[] = claimStatusesList.map((status: ClaimsStatus) => ({
					label: getClaimStatusFromBffStatus(status),
					value: status.toString()
				}));

				setPassGroupOptions(passGroupOpts);
				setStateOptions(stateOpts);
				setBenefitCodeOptions(benefitCodeOpts);
				setClaimStatusOptions(claimStatusOpts);
			} catch (err) {
				console.error("Error fetching form data:", err);
				toast("Error", "Failed to load form options", "error");
			} finally {
				setIsLoadingFormData(false);
			}
		};

		if (isOpen) {
			fetchFormData();
		}
	}, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleSubmit = async () => {
		const response = await createReservation(
			userId,
			passGroupId || undefined, // Convert empty string to undefined (wildcard)
			benefitCode || undefined, // Convert empty string to undefined
			state || undefined, // Pass state if provided
			claimStatus // Pass claim status if provided
		);

		if (response) {
			// Reset form
			setUserId(defaultUserId || 0);
			setPassGroupId("");
			setBenefitCode("");
			setState("");
			setClaimStatus(undefined);
			setSelectedPassGroup(null);
			setSelectedState(null);
			setSelectedBenefitCode(null);
			setSelectedClaimStatus(null);
			onSuccess();
			onClose();
		}
	};

	const handleCancel = () => {
		// Reset form (but keep defaultUserId if provided)
		setUserId(defaultUserId || 0);
		setPassGroupId("");
		setBenefitCode("");
		setState("");
		setClaimStatus(undefined);
		setSelectedPassGroup(null);
		setSelectedState(null);
		setSelectedBenefitCode(null);
		setSelectedClaimStatus(null);
		onClose();
	};

	const isFormValid = userId > 0 && claimStatus != null && !isLoadingFormData;

	return (
		<CustomModal
			isOpen={isOpen}
			isCentered={true}
			closeOnOverlayClick={false}
			onCancel={handleCancel}
			onContinue={handleSubmit}
			size="4xl"
			title="Create Automated Claim Assignment"
			continueText="Create"
			continueColorScheme="orange"
			continueDisabled={!isFormValid || isLoading}
			continueLoading={isLoading}
			content={
				<Box py={4}>
					{/* Show analyst info if pre-selected */}
					{defaultUserId && userName && (
						<Box mb={4} p={3} bg="blue.50" borderRadius="md">
							<Text fontSize="sm" color="gray.700">
								<Text as="span" fontWeight="semibold">
									Creating reservation for:
								</Text>{" "}
								{userName}
							</Text>
						</Box>
					)}

					{/* Form fields in horizontal layout */}
					<HStack spacing={4} align="flex-start" mb={4}>
						<FormControl isRequired flex={1}>
							<FormLabel fontSize="sm" fontWeight="semibold" mb={1}>
								Claim Status
							</FormLabel>
							<ChakraReactSelect
								size="sm"
								value={selectedClaimStatus}
								options={claimStatusOptions}
								onChange={(option: SingleValue<SelectOption>) => {
									setSelectedClaimStatus(option);
									setClaimStatus(option?.value ? parseInt(option.value) : undefined);
								}}
								placeholder="Search or select Status"
								isDisabled={isLoadingFormData}
								isLoading={isLoadingFormData}
								isClearable
								menuPortalTarget={document.body}
								menuPosition="fixed"
								styles={{
									menuPortal: (provided) => ({
										...provided,
										zIndex: 9999
									})
								}}
								chakraStyles={{
									control: (provided) => ({
										...provided,
										borderColor: "#bbbbbb",
										borderRadius: "3px"
									})
								}}
							/>
						</FormControl>
						<FormControl flex={1}>
							<FormLabel fontSize="sm" fontWeight="semibold" mb={1}>
								PASS Group Id
							</FormLabel>
							<ChakraReactSelect
								size="sm"
								value={selectedPassGroup}
								options={passGroupOptions}
								onChange={(option: SingleValue<SelectOption>) => {
									setSelectedPassGroup(option);
									setPassGroupId(option?.value || "");
								}}
								placeholder="Search or select PASS Group"
								isDisabled={isLoadingFormData}
								isLoading={isLoadingFormData}
								isClearable
								menuPortalTarget={document.body}
								menuPosition="fixed"
								styles={{
									menuPortal: (provided) => ({
										...provided,
										zIndex: 9999
									})
								}}
								chakraStyles={{
									control: (provided) => ({
										...provided,
										borderColor: "#bbbbbb",
										borderRadius: "3px"
									})
								}}
							/>
						</FormControl>

						<FormControl flex={1}>
							<FormLabel fontSize="sm" fontWeight="semibold" mb={1}>
								State
							</FormLabel>
							<ChakraReactSelect
								size="sm"
								value={selectedState}
								options={stateOptions}
								onChange={(option: SingleValue<SelectOption>) => {
									setSelectedState(option);
									setState(option?.value || "");
								}}
								placeholder="Search or select State"
								isDisabled={isLoadingFormData}
								isLoading={isLoadingFormData}
								isClearable
								menuPortalTarget={document.body}
								menuPosition="fixed"
								styles={{
									menuPortal: (provided) => ({
										...provided,
										zIndex: 9999
									})
								}}
								chakraStyles={{
									control: (provided) => ({
										...provided,
										borderColor: "#bbbbbb",
										borderRadius: "3px"
									})
								}}
							/>
						</FormControl>

						<FormControl flex={1}>
							<FormLabel fontSize="sm" fontWeight="semibold" mb={1}>
								Matter Billing Code
							</FormLabel>
							<ChakraReactSelect
								size="sm"
								value={selectedBenefitCode}
								options={benefitCodeOptions}
								onChange={(option: SingleValue<SelectOption>) => {
									setSelectedBenefitCode(option);
									setBenefitCode(option?.value || "");
								}}
								placeholder="Search or select Code"
								isDisabled={isLoadingFormData}
								isLoading={isLoadingFormData}
								isClearable
								menuPortalTarget={document.body}
								menuPosition="fixed"
								styles={{
									menuPortal: (provided) => ({
										...provided,
										zIndex: 9999
									})
								}}
								chakraStyles={{
									control: (provided) => ({
										...provided,
										borderColor: "#bbbbbb",
										borderRadius: "3px"
									})
								}}
							/>
						</FormControl>
					</HStack>

					<Box mt={3}>
						<Text fontSize="xs" color="gray.600">
							Any field left blank is treated as a wildcard (matches all values for that field)
						</Text>
					</Box>
				</Box>
			}
		/>
	);
}
